/**
 * The capture screen — the whole reason this application exists.
 *
 * Laid out as the checklist from `@rush/sync-core/checklist`, one card per shot slot, with
 * the "why" text kept visible rather than tucked behind a help icon. The template is not a
 * suggestion: the shots it asks for are the ones an adjuster will look for, and a rep who
 * understands *why* the drip edge matters takes a usable photo of it the first time instead
 * of driving back.
 *
 * Two behaviours here are load-bearing and are the ones RUS-8 is judged on:
 *
 *  1. **Capture never waits on the network.** The handler hashes, stores, and enqueues
 *     locally, then returns. Sync is kicked off afterwards without being awaited, so
 *     turning off the radio mid-capture changes nothing about whether the photo exists.
 *  2. **Every photo shows its own state.** Not one global spinner — per-photo, because
 *     "some of your work is uploaded" is useless to a rep deciding whether to drive away.
 *
 * Capture uses a plain file input with `capture="environment"`. It opens the rear camera on
 * both iOS and Android, needs no permission prompt of its own, and costs no dependency —
 * `getUserMedia` would buy a live preview in exchange for a permissions dance and a
 * MediaStream to manage. ADR-0001 §3.2: boring technology, on purpose.
 */

import { useRef, useState } from 'react';

import type { PhotoRow } from '../db/database.js';
import type { JobView, PhotoStatus } from '../db/repository.js';
import { Chip, PhotoThumbnail } from './components.js';
import type { FieldRepository } from '../db/repository.js';
import { formatCapturedAt, photoStatus } from './status-text.js';
import { colors, styles } from './styles.js';

export function InspectionScreen({
  repository,
  view,
  statuses,
  onCapture,
  onBack,
}: {
  repository: FieldRepository;
  view: JobView;
  statuses: ReadonlyMap<string, PhotoStatus>;
  onCapture: (slotId: string, bytes: Uint8Array, contentType: string) => Promise<void>;
  onBack: () => void;
}) {
  const { job, progress } = view;
  const [error, setError] = useState<string | undefined>(undefined);
  const [capturing, setCapturing] = useState<string | undefined>(undefined);

  const bySlot = new Map<string, PhotoRow[]>();
  for (const photo of view.photos) {
    const bucket = bySlot.get(photo.slotId);
    if (bucket === undefined) bySlot.set(photo.slotId, [photo]);
    else bucket.push(photo);
  }

  const handleFile = async (slotId: string, file: File): Promise<void> => {
    setCapturing(slotId);
    setError(undefined);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      await onCapture(slotId, bytes, file.type === '' ? 'image/jpeg' : file.type);
    } catch (cause: unknown) {
      // Surfaced, not swallowed. If a capture failed, the rep has to know to reshoot —
      // silently dropping it is how a claim arrives missing the one photo that mattered.
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setCapturing(undefined);
    }
  };

  return (
    <>
      <button type="button" style={styles.button} onClick={onBack}>
        ← All inspections
      </button>

      <div>
        <h2 style={{ ...styles.cardTitle, fontSize: '1.25rem' }}>{job.address}</h2>
        <p style={styles.cardMeta}>
          {progress.template.label} · {job.homeownerName || 'homeowner not recorded'} ·{' '}
          {job.claimNumber || 'no claim number'}
        </p>
        <p style={styles.cardMeta} data-testid="checklist-progress">
          {String(progress.requiredSatisfied)} of {String(progress.requiredTotal)} required shots
          captured
          {progress.complete ? ' — checklist complete' : ''}
        </p>
      </div>

      {error !== undefined && (
        <p style={{ ...styles.cardMeta, color: '#ff9c9c' }} role="alert">
          Capture failed: {error}
        </p>
      )}

      {progress.slots.map((slotProgress) => {
        const photos = bySlot.get(slotProgress.slot.id) ?? [];
        return (
          <SlotCard
            key={slotProgress.slot.id}
            repository={repository}
            slotId={slotProgress.slot.id}
            label={slotProgress.slot.label}
            why={slotProgress.slot.why}
            required={slotProgress.slot.required}
            satisfied={slotProgress.satisfied}
            photos={photos}
            statuses={statuses}
            busy={capturing === slotProgress.slot.id}
            onFile={handleFile}
          />
        );
      })}
    </>
  );
}

function SlotCard({
  repository,
  slotId,
  label,
  why,
  required,
  satisfied,
  photos,
  statuses,
  busy,
  onFile,
}: {
  repository: FieldRepository;
  slotId: string;
  label: string;
  why: string;
  required: boolean;
  satisfied: boolean;
  photos: readonly PhotoRow[];
  statuses: ReadonlyMap<string, PhotoStatus>;
  busy: boolean;
  onFile: (slotId: string, file: File) => Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);

  return (
    <section style={{ ...styles.slot, ...(satisfied ? { borderColor: '#1f7a47' } : {}) }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>{label}</h3>
        <Chip
          status={
            satisfied
              ? { tone: 'good', text: 'captured' }
              : required
                ? { tone: 'warn', text: 'required' }
                : { tone: 'info', text: 'optional' }
          }
        />
      </div>

      <p style={styles.slotWhy}>{why}</p>

      {photos.length > 0 && (
        <div style={styles.thumbRow}>
          {photos.map((photo) => (
            <figure key={photo.photoId} style={{ margin: 0, display: 'grid', gap: '0.25rem' }}>
              <PhotoThumbnail repository={repository} photo={photo} />
              <figcaption style={{ display: 'grid', gap: '0.15rem', justifyItems: 'start' }}>
                <Chip
                  status={photoStatus(statuses.get(photo.photoId)?.state ?? 'queued')}
                />
                <span style={{ fontSize: '0.7rem', color: colors.faint }}>
                  {formatCapturedAt(photo.capturedAt)}
                  {photo.redacted ? ' · redacted' : ''}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      <input
        ref={input}
        type="file"
        accept="image/*"
        // Rear camera. Ignored on desktop, where it falls back to a file picker — which is
        // what you want when testing the flow without a phone in your hand.
        capture="environment"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Reset first: picking the same file twice in a row fires no change event
          // otherwise, and a rep reshooting the same slot would silently get nothing.
          event.target.value = '';
          if (file !== undefined) void onFile(slotId, file);
        }}
      />

      <button
        type="button"
        style={{ ...styles.button, ...(satisfied ? {} : styles.buttonPrimary) }}
        disabled={busy}
        data-testid={`capture-${slotId}`}
        onClick={() => {
          input.current?.click();
        }}
      >
        {busy ? 'Saving…' : photos.length > 0 ? 'Take another' : 'Take photo'}
      </button>
    </section>
  );
}
